import { pick, omit } from "lodash";
import {
    Client,
    OnDemandUser,
    PassioRider,
    Program,
    RiderProgram,
} from "src/modules/auth/types";
import { ProfileFormData } from "./types";
import { dateTimeFormat } from "src/utils/constants";
import { getInitialValues } from "../auth/pages/signup/helpers";
import moment from "moment";

// selected fields for profile update
export const accessibleFields = ["hasPCA", "usesWheelchair", "wheelChairType"];
export const dataFields = [
    "id",
    "email",
    "phone",
    "defaultProgramMembershipID",
    "defaultProgramName",
    "accessibility",
];

export const navigatorDataFields = [
    "riderId",
    "address",
    "programs",
    "validationCode",
    "defaultServiceId",
];

export function initProfile(
    client: Client,
    programs: Program[] = [],
    availableFundingSources: string[] = [],
    newRider = false,
    isNavigator = false
): ProfileFormData {
    let profile: ProfileFormData = omit(getInitialValues(), [
        "password",
        "confirmPassword",
    ]);

    if (client && Object.keys(client).length) {
        const [firstName, ...lastName] = client?.name?.split(" ");
        profile = {
            ...pick(client, dataFields),
            firstName,
            lastName: lastName.join(" "),
            birthdate: client.birthdate
                ? moment(client.birthdate).toDate()
                : null,
        };
    }
    if (isNavigator) {
        profile = {
            ...profile,
            ...pick(client, navigatorDataFields),
        };
    }

    if (newRider) {
        profile.defaultProgramName = availableFundingSources[0];
    }

    return profile;
}

export interface FormattedProfile {
    rider: Partial<PassioRider>;
    paraplan: Partial<Client> | any;
    onDemandUser: Partial<OnDemandUser>;
}

export function formatProfile(
    values: ProfileFormData,
    isNavigator = true
): FormattedProfile {
    let profile = { rider: {}, onDemandUser: {}, paraplan: {} };

    if (!isNavigator) {
        profile.paraplan = {
            ...pick(values, dataFields),
            accessibility: pick(values.accessibility, accessibleFields),
            name: `${values.firstName} ${values.lastName}`,
            birthdateEpoch: moment(values.birthdate).unix(),
        };
    }

    profile.onDemandUser = {
        name: `${values.firstName} ${values.lastName}`,
        birthdate: moment.isDate(values.birthdate)
            ? moment(values.birthdate).format(dateTimeFormat)
            : null,
        accessibility: JSON.stringify(values.accessibility),
        address: JSON.stringify(values.address),
        ...pick(values, ["id", "phone"]),
    };

    // convert programs back, we only need two fields
    const programs = values?.programs?.reduce((acc: RiderProgram[], item) => {
        acc.push({
            serviceId: item.databaseID,
            membershipId: item.membershipID,
        });
        return acc;
    }, []);

    profile.rider = {
        id: values.riderId,
        programs: programs || [],
        defaultServiceId: values.defaultServiceId || "",
        validationCode: values.validationCode
            ? values.validationCode.trim()
            : null,
    };

    return profile;
}
